import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, PanResponder, type LayoutChangeEvent } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { useTheme } from '../../../theme/useTheme'
import { BRAND_COLOR_HUE_GRADIENT_STOPS } from '../../constants/accountAdmin.constants'
import {
  hexToHsv,
  hsvToHex,
  hueToPureHex,
  normalizeHexColor,
} from '../../helpers/brandColorPicker.helper'
import {
  getBrandColorPaletteStyles,
  getHueThumbOffsetStyle,
  getSaturationValueThumbStyle,
} from './BrandColorPalette.styles'

const OVERLAY_TRANSPARENT = 'rgba(0,0,0,0)'

export interface BrandColorPaletteProps {
  value: string
  onColorChange: (hex: string) => void
  label: string
  saturationBrightnessA11yLabel: string
  hueA11yLabel: string
}

const DEFAULT_HSV = { h: 270, s: 0.82, v: 0.71 }

function initialHsvFromHex(hex: string): { h: number; s: number; v: number } {
  const n = normalizeHexColor(hex)
  if (!n) return DEFAULT_HSV
  return hexToHsv(n) ?? DEFAULT_HSV
}

export const BrandColorPalette: React.FC<BrandColorPaletteProps> = ({
  value,
  onColorChange,
  label,
  saturationBrightnessA11yLabel,
  hueA11yLabel,
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getBrandColorPaletteStyles(theme), [theme])

  const [hsv, setHsv] = useState<{ h: number; s: number; v: number }>(() =>
    initialHsvFromHex(value)
  )
  const { h, s, v } = hsv

  const panelLayout = useRef({ w: 1, h: 1 })
  const hueLayout = useRef({ w: 1 })
  const onColorChangeRef = useRef(onColorChange)
  onColorChangeRef.current = onColorChange
  const hsvRef = useRef(hsv)
  hsvRef.current = hsv

  const applySvRef = useRef<((x: number, y: number) => void) | undefined>(undefined)
  const applyHueRef = useRef<((x: number) => void) | undefined>(undefined)

  useEffect(() => {
    const normalized = normalizeHexColor(value)
    if (!normalized) return
    const cur = hsvRef.current
    const fromHsv = hsvToHex(cur.h, cur.s, cur.v)
    if (normalizeHexColor(fromHsv) === normalized) return
    const parsed = hexToHsv(normalized)
    if (!parsed) return
    setHsv(parsed)
  }, [value])

  const applySv = useCallback((x: number, y: number) => {
    const { w, h: panelH } = panelLayout.current
    if (w <= 0 || panelH <= 0) return
    const nextS = Math.max(0, Math.min(1, x / w))
    const nextV = Math.max(0, Math.min(1, 1 - y / panelH))
    const nextH = hsvRef.current.h
    setHsv({ h: nextH, s: nextS, v: nextV })
    onColorChangeRef.current(hsvToHex(nextH, nextS, nextV))
  }, [])

  const applyHue = useCallback((x: number) => {
    const { w } = hueLayout.current
    if (w <= 0) return
    const nextH = Math.max(0, Math.min(360, (x / w) * 360))
    const { s: curS, v: curV } = hsvRef.current
    setHsv({ h: nextH, s: curS, v: curV })
    onColorChangeRef.current(hsvToHex(nextH, curS, curV))
  }, [])

  applySvRef.current = applySv
  applyHueRef.current = applyHue

  const panelPan = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (e) => {
        applySvRef.current?.(e.nativeEvent.locationX, e.nativeEvent.locationY)
      },
      onPanResponderMove: (e) => {
        applySvRef.current?.(e.nativeEvent.locationX, e.nativeEvent.locationY)
      },
    })
  ).current

  const huePan = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (e) => {
        applyHueRef.current?.(e.nativeEvent.locationX)
      },
      onPanResponderMove: (e) => {
        applyHueRef.current?.(e.nativeEvent.locationX)
      },
    })
  ).current

  const onPanelLayout = useCallback((e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout
    panelLayout.current = { w: width, h: height }
  }, [])

  const onHueLayout = useCallback((e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout
    hueLayout.current = { w: width }
  }, [])

  const pureHueHex = useMemo(() => hueToPureHex(h), [h])

  const svThumbStyle = useMemo(
    () => getSaturationValueThumbStyle(theme, s, v),
    [theme, s, v]
  )

  const hueThumbOffset = useMemo(() => getHueThumbOffsetStyle(h), [h])

  return (
    <View style={styles.block}>
      <Text style={styles.label}>{label}</Text>
      <View
        accessibilityLabel={saturationBrightnessA11yLabel}
        accessibilityRole="adjustable"
        onLayout={onPanelLayout}
        style={styles.panel}
        {...panelPan.panHandlers}
      >
        <LinearGradient
          colors={['#FFFFFF', pureHueHex]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={styles.panelLayers}
        />
        <LinearGradient
          colors={[OVERLAY_TRANSPARENT, '#000000']}
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          style={styles.panelLayers}
        />
        <View style={svThumbStyle} />
      </View>
      <View
        accessibilityLabel={hueA11yLabel}
        accessibilityRole="adjustable"
        onLayout={onHueLayout}
        style={styles.hueStrip}
        {...huePan.panHandlers}
      >
        <LinearGradient
          colors={[...BRAND_COLOR_HUE_GRADIENT_STOPS]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={styles.hueStripLayers}
        />
        <View style={[styles.thumbHue, hueThumbOffset]} />
      </View>
    </View>
  )
}
